<template>
  <div class="sp-app-filter">
    <template v-if="isVariantFilterBar">
      <component
        :is="FilterBarWrapper"
        ref="filterInstance"
        v-bind="attrs"
        :model-value="model"
        horizontal-align="left"
        :filter="filter"
        :non-clearable="nonClearable"
        :open="open"
        @update:model-value="handleFilterModelUpdate"
        @update:open="open = $event"
        @update:active="active = $event"
      />
    </template>

    <template v-else>
      <component
        :is="filter"
        ref="filterInstance"
        v-bind="attrs"
        :model-value="model"
        :non-clearable="nonClearable"
        :open="open"
        @update:model-value="handleFilterModelUpdate"
      />
    </template>
  </div>
</template>

<script>
import DateFilter from "#ce/apps/shared/filters/date/components/DateFilter.ce.vue";
import DistanceFilter from "#ce/apps/shared/filters/distance/components/DistanceFilter.ce.vue";
import InsurancePlanFilter from "#ce/apps/shared/filters/insurance-plan/components/InsurancePlanFilter.ce.vue";
import ProviderFilter from "#ce/apps/shared/filters/provider/components/ProviderFilter.ce.vue";
import SpecialtyFilter from "#ce/apps/shared/filters/specialty/components/SpecialtyFilter.ce.vue";
import VisitTypeFilter from "#ce/apps/shared/filters/visit-type/components/VisitTypeFilter.ce.vue";

/**
 * All available filters.
 * The filter instance will be dynamically loaded based on the filter type prop.
 *
 * @type {Object}
 */
const availableFilters = {
  insurance: InsurancePlanFilter,
  distance: DistanceFilter,
  date: DateFilter,
  specialty: SpecialtyFilter,
  provider: ProviderFilter,
  visitType: VisitTypeFilter,
};
</script>

<script setup>
import { computed, nextTick, onMounted, ref, useAttrs, useTemplateRef, watch } from "vue";
import { useExpose } from "#ce/composables/useExpose";
import { useHostElement } from "#ce/composables/useHostElement";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { booleanAttribute, toBoolean, trimToUndefined } from "#ce/utils/props";
import { formAssociatedHost } from "#ce/framework/utils";
import FilterBarWrapper from "./components/FilterBarWrapper.ce.vue";

// The filters submit form values via useForm() on this app's host.
defineOptions(formAssociatedHost());

const emit = defineEmits(["update-active", "update-open", "change", "close"]);
const { emitBubbling } = useHostEmit();

const attrs = useAttrs();

const props = defineProps({
  /**
   * The type of the filter.
   *
   * @type {String} - The type of the filter. One of: insurance, distance, date, specialty, provider.
   * @required
   */
  type: {
    type: String,
    validator: (value) => Object.keys(availableFilters).includes(value),
    required: true,
  },
  variant: {
    type: String,
    default: "default",
    validator: (value) => ["default", "filter-bar"].includes(value),
  },
  open: booleanAttribute(),
  /**
   * When set, the filter can't be cleared (legal requirement, see
   * `<sp-required-filters-modal>`). Bound to the wrapper/filter and used to guard
   * the `deactivate()` method exposed for the legacy filter menu's "Reset All".
   */
  nonClearable: booleanAttribute(),
});

const model = ref(undefined);

// The filters announce their initial model as an immediate update:model-value at setup; the
// host's change reports only what happens after that.
const isSettled = ref(false);

function markSettled() {
  isSettled.value = true;
}

onMounted(() => nextTick(markSettled));

// A scripted write's round-trip through the filter can carry a fresh object (ProviderFilter
// rebuilds a FormData per recomputation), which the identity guard below cannot recognize — the
// scripted entry points hold this window open through their watcher flush instead.
let isScriptedWrite = false;

function writeSilently(write) {
  isScriptedWrite = true;
  write();
  nextTick(() => (isScriptedWrite = false));
}

// The inner filter reports the user's pick through update:model-value. The exposed `value`
// setter and the mount-time attribute seed write the model silently (their round-trips
// arrive back here).
//
// `options.seeded` marks an update that carries the value the mount was given rather than a pick:
// it moves the model and dispatches nothing. A filter that resolves its seed through a request
// sets it, since that update lands after the settle tick.
function handleFilterModelUpdate(value, options) {
  if (value === model.value) {
    return;
  }

  model.value = value;

  if (isSettled.value && !isScriptedWrite && !options?.seeded) {
    emitBubbling("change", value);
  }
}

const isVariantFilterBar = computed(() => props.variant === "filter-bar");

const filter = computed(() => availableFilters[trimToUndefined(props.type)]);

const isNonClearable = computed(() => toBoolean(props.nonClearable));

const active = ref(false);

watch(active, (value) => {
  emit("update-active", value);
});

const open = ref(toBoolean(props.open));
watch(
  () => props.open,
  (value) => (open.value = toBoolean(value)),
);

const host = useHostElement();

watch(open, (value) => {
  emit("update-open", value);

  if (value && isVariantFilterBar.value) {
    /**
     * Scroll the filter into view when the filter bar is opened.
     */
    scrollIntoViewIfNeeded();
  } else if (!value) {
    removeOpenAttribute();
    emit("close");
  }
});

/**
 * Remove the open attribute from the root element.
 * This is necessary to keep the reactivity of the open attribute.
 *
 * TODO: Remove once the filter menu is migrated to the new filter.
 */
function removeOpenAttribute() {
  host?.removeAttribute("open");
}

function scrollIntoViewIfNeeded() {
  host?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
}

const filterInstance = useTemplateRef("filterInstance");

// Both variants present the same surface: the bare filter in the default variant,
// and FilterBarWrapper forwarding the inner filter's surface in the filter-bar one.
const filterInstanceProps = computed(() => filterInstance.value?.props);

const { exposeProperties, exposeMethods } = useExpose();

/**
 * Compatability with the filter menu.
 * TODO: Remove once all filters are migrated to the new filter.
 */
exposeProperties({
  isFilterApp: { value: true },
  prependIcon: {
    get: () => filterInstanceProps.value?.prependIcon,
  },
  value: {
    set: (value) => writeSilently(() => (model.value = trimToUndefined(value))),
    get: () => model.value,
  },
  active: {
    get: () => active.value,
  },
  label: {
    get: () => attrs.label,
  },
  /**
   * Multi-value filters (the Provider panel) own several search params. The
   * doctor-finder bridge reads this list to learn which params the one element
   * carries. Single-value filters leave it undefined and fall back to `name`.
   */
  filterNames: {
    get: () => filterInstance.value?.filterNames,
  },
  /**
   * The current selection's display wording, read by the doctor-finder bridge for the
   * search analytics. Undefined for filters that do not resolve their selection to
   * text, and while nothing is selected.
   *
   * TODO: Remove with the rest of this bridge — https://github.com/combinaut/sparkle/issues/29500
   */
  selectedText: {
    get: () => filterInstance.value?.selectedText,
  },
});

/**
 * Compatability with the filter menu.
 * TODO: Remove once all filters are migrated to the new filter.
 */
exposeMethods({
  /**
   * Clears the filter, dispatching one host `change` with the cleared state. A filter
   * that rebuilds an empty model from the clear (the Provider panel) supplies that
   * dispatch through `handleFilterModelUpdate`. When no rebuilt model has arrived by
   * the flush, the host dispatches `undefined` itself. A no-op when nothing is applied
   * or the filter is non-clearable.
   */
  deactivate() {
    if (isNonClearable.value || model.value === undefined) {
      return;
    }

    model.value = undefined;
    nextTick(() => {
      if (model.value === undefined) {
        emitBubbling("change", undefined);
      }
    });
  },
  /**
   * Restore a multi-value filter's sub-selections from URL params (deep-link,
   * back/forward). No-op for single-value filters that don't implement it.
   */
  setFilterValues(values) {
    writeSilently(() => filterInstance.value?.setFilterValues?.(values));
  },
  /**
   * Narrows a filter's own options to the given provider-search filters, for the
   * filters a client configures under `filter_scopes`. No-op for filters whose
   * options do not depend on the current search.
   *
   * The scope belongs on the filter that has options to narrow, not on this host.
   * TODO: Replace with a declarative prop — https://github.com/combinaut/sparkle/issues/29500
   *
   * @returns {Promise<Boolean>|undefined} The filter's report of whether its narrowed options
   *   arrived. Undefined where the filter has no options to narrow, or none has resolved yet.
   */
  setScope(values) {
    return filterInstance.value?.setScope?.(values);
  },
});

onMounted(() => {
  if (attrs.value) {
    writeSilently(() => (model.value = trimToUndefined(attrs.value)));
  }
});
</script>

<style>
:host {
  display: block;
  width: 100%;
}

/* Open the filter-bar menus with the same small gap below the chip as the
   legacy filter menus (the native <dialog> ones like Availability sit ~0.5rem
   off their chip). The SpPopup default is tighter (4px) and reads as "stuck"
   next to the roomier legacy menus. One declaration covers every filter-bar
   type (provider, specialty, distance, …) via the inherited token. */
:host([variant="filter-bar"]) {
  --sp-comp-popup-offset: 0.5rem;
}

:host(:focus),
:host(:focus-visible) {
  outline: none;
  box-shadow: none !important;
}
</style>
